import React, { useId, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Archive,
  BarChart3,
  BedDouble,
  Briefcase,
  Building2,
  Calendar,
  CalendarRange,
  CheckSquare,
  ChevronDown,
  ClipboardCheck,
  ClipboardList,
  Coins,
  FileCheck,
  FileEdit,
  FileText,
  Globe,
  Handshake,
  Home,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  LineChart,
  ListChecks,
  LogOut,
  Mail,
  Map,
  Moon,
  Package,
  Receipt,
  RefreshCw,
  Scale,
  ScrollText,
  Settings,
  Settings2,
  ShieldCheck,
  Sofa,
  Sun,
  Target,
  Timer,
  TrendingDown,
  TrendingUp,
  UserCog,
  Users,
  Wallet,
  Zap,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { REPORTS_TO, calendarNavState } from "./workOrganizationNav";
import { BEXIO_NAV_ITEMS, isBexioPath } from "./bexio/bexioNav";
import OrganizationIdentity from "./sidebar/OrganizationIdentity";
// The approved Bexio brand mark, committed to the repo rather than fetched:
// branding a partner renders must not depend on their CDN being up, and the
// bytes are the ones signed off rather than whatever that URL serves later.
import bexioBrandIcon from "../../assets/bexio-bx-icon.webp";

/**
 * Icons for the Bexio rows, chosen HERE rather than in `bexioNav`.
 *
 * `bexioNav` stays the single source of truth for which Bexio pages exist and
 * where they point — the sidebar and the workspace tab bar both read it, which is
 * the whole reason it exists. Presentation is a different question: the tab bar
 * renders labels only, so an icon is a sidebar concern, and keeping the mapping
 * on this side means the redesign never edits the shared routing config.
 *
 * Keyed by `item.key`, so a Bexio page added to the shared list still appears
 * here — with a neutral fallback icon rather than nothing at all.
 */
const BEXIO_ICONS = {
  overview: LayoutDashboard,
  drafts: FileEdit,
  invoices: FileText,
  contacts: Users,
  reconciliation: Scale,
  "sync-runs": RefreshCw,
  settings: Settings,
};

/**
 * The navigation, declared as data.
 *
 * Sections render in this order and each item lands in exactly one of them. The
 * previous version spelled all of this out in JSX, which is how the same page
 * ends up listed twice, or listed nowhere: as a list it can be — and is — checked
 * mechanically by the tests.
 *
 * `kind` marks the two rows React Router cannot decide between on its own; see
 * `renderItem`. Everything else is a plain `NavLink`.
 */
const SECTIONS = [
  {
    key: "dashboard",
    title: "Dashboard",
    icon: LayoutDashboard,
    accent: { light: "text-violet-600", dark: "text-violet-300" },
    // `/admin/dashboard` renders the same page as `/admin` and nothing links
    // to it; see the `owns` note above `SECTION_ROUTES`.
    owns: ["/admin/dashboard"],
    items: [
      { to: "/admin", label: "Unternehmensübersicht", icon: Home, end: true },
      { to: "/admin/operations", label: "Co-Living-Dashboard", icon: Sofa },
      {
        to: "/admin/business-apartments-dashboard",
        label: "Business-Apartment-Dashboard",
        icon: Briefcase,
      },
      { to: "/admin/portfolio-map", label: "Portfolio-Karte", icon: Map },
    ],
  },
  {
    key: "immobilien",
    title: "Immobilien",
    icon: Building2,
    accent: { light: "text-sky-600", dark: "text-sky-300" },
    // A unit detail is reached FROM Einheiten and has no row of its own.
    owns: ["/admin/units", "/admin/objekte-dashboard"],
    items: [
      { to: "/admin/properties", label: "Liegenschaften", icon: Building2 },
      { to: "/admin/apartments", label: "Einheiten", icon: LayoutGrid },
      { to: "/admin/rooms", label: "Co-Living-Zimmer", icon: BedDouble },
      { to: "/admin/occupancy", label: "Belegung", icon: CalendarRange },
      { to: "/admin/inventory", label: "Inventar", icon: Package },
      { to: "/admin/documents", label: "Dokumente", icon: FileText },
    ],
  },
  {
    key: "vermietung",
    title: "Vermietung",
    icon: Globe,
    accent: { light: "text-emerald-600", dark: "text-emerald-300" },
    items: [
      { to: "/admin/listings", label: "Website-Listings", icon: Globe },
      { to: "/admin/applications", label: "Bewerbungen", icon: ClipboardCheck },
      { to: "/admin/leads", label: "Leads", icon: Target },
      { to: "/admin/tenants", label: "Mieter", icon: Users },
      {
        to: "/admin/tenancies",
        label: "Mietverhältnisse",
        icon: ScrollText,
      },
      { to: "/admin/nachweise", label: "Nachweise & Pflichten", icon: FileCheck },
    ],
  },
  {
    key: "arbeitsorganisation",
    title: "Arbeitsorganisation",
    icon: ClipboardList,
    accent: { light: "text-amber-600", dark: "text-amber-300" },
    // A work report is opened from Rapporte, which is itself a query on the
    // calendar — so neither the report nor its list carries a `to` to match.
    owns: ["/admin/work-reports"],
    items: [
      { to: "/admin/todo-lists", label: "To-do-Listen", icon: ListChecks },
      { to: "/admin/tasks", label: "Aufgaben", icon: CheckSquare },
      // One route, two rows — see `renderItem` and `workOrganizationNav`.
      { kind: "calendar", label: "Kalender", icon: Calendar },
      { kind: "reports", label: "Rapporte", icon: Timer },
      { to: "/admin/responsibilities", label: "Verantwortlichkeiten", icon: UserCog },
      // The first row to carry `roles`. Every endpoint behind the page is
      // admin-only, so a manager following this link would reach a page that
      // can only refuse — see `isItemVisibleForRole`.
      {
        to: "/admin/automations",
        label: "Automatisierungen",
        icon: Zap,
        roles: ["admin"],
      },
    ],
  },
  {
    key: "vermieter",
    title: "Vermieter & Verwaltungen",
    icon: Handshake,
    accent: { light: "text-teal-600", dark: "text-teal-300" },
    owns: ["/admin/contracts"],
    items: [
      { to: "/admin/owners", label: "Eigentümer", icon: KeyRound },
      { to: "/admin/landlords", label: "Verwaltungen", icon: Landmark },
      { to: "/admin/bewirtschafter", label: "Bewirtschafter", icon: Briefcase },
    ],
  },
  {
    key: "analyse",
    title: "Analyse",
    icon: BarChart3,
    accent: { light: "text-indigo-600", dark: "text-indigo-300" },
    items: [
      { to: "/admin/revenue-analytics", label: "Umsatz & Miete", icon: TrendingUp },
      { to: "/admin/performance", label: "Performance", icon: LineChart },
      { to: "/admin/break-even", label: "Break-Even", icon: Scale },
      { to: "/admin/prognose", label: "Prognose", icon: BarChart3 },
    ],
  },
  {
    key: "bexio",
    title: "Bexio",
    // The one section headed by a real brand mark rather than a lucide glyph.
    // `RefreshCw` stood in for it until the artwork was approved; it still draws
    // the Sync-Läufe row below, via BEXIO_ICONS.
    //
    // No `accent`: the colour of a brand mark is the brand's, not ours.
    brandIcon: bexioBrandIcon,
    // No `owns`: the whole workspace belongs to this group and `isBexioPath`
    // already answers that question — see `getSidebarSectionForLocation`.
    // Built from the SHARED list so the sidebar and the workspace tab bar can
    // never offer different pages. No Bexio URL is written in this file.
    items: BEXIO_NAV_ITEMS.map((item) => ({
      to: item.to,
      label: item.label,
      end: item.end,
      icon: BEXIO_ICONS[item.key] || FileText,
    })),
  },
  {
    key: "finanzen",
    title: "Finanzen",
    icon: Wallet,
    accent: { light: "text-rose-600", dark: "text-rose-300" },
    owns: ["/admin/rechnungen-dashboard"],
    // ACTIVE finance surfaces. Ausgaben reads unit costs and Belege reads the
    // receipt store — neither has anything to do with invoicing, and
    // Rechnungseinstellungen is read by the Bexio invoice as well as the native
    // PDF. Marking any of them "old" would be untrue, which is why only the two
    // rows below carry the legacy treatment.
    items: [
      { to: "/admin/ausgaben", label: "Ausgaben", icon: TrendingDown },
      { to: "/admin/belege", label: "Belege", icon: Receipt },
      { to: "/admin/kautionen", label: "Kautionen", icon: ShieldCheck },
      { to: "/admin/billing-settings", label: "Rechnungseinstellungen", icon: Settings2 },
    ],
    legacy: {
      badge: "ALT",
      note: "Alte Rechnungswelt (vor Bexio)",
      items: [
        { to: "/admin/invoices", label: "Historische Rechnungen", icon: Archive },
        { to: "/admin/revenue", label: "Einnahmen", icon: Coins },
      ],
    },
  },
  {
    key: "administration",
    title: "Administration",
    icon: ShieldCheck,
    accent: { light: "text-zinc-500", dark: "text-slate-400" },
    // Roles are assigned inside the Benutzer page today and there is no
    // roles/permissions route to link to; a label without a target is a promise
    // the product does not keep. E-Mail-Absender (T1b) is the organisation's
    // sender configuration, and like Automatisierungen every endpoint behind it
    // is admin-only — see `isItemVisibleForRole`.
    items: [
      { to: "/admin/users", label: "Benutzer", icon: Users },
      {
        to: "/admin/email-settings",
        label: "E-Mail-Absender",
        icon: Mail,
        roles: ["admin"],
      },
    ],
  },
];

/* ─── Which section owns the current route ─────────────────────────── */

/**
 * Every routed path the navigation owns, longest first.
 *
 * Derived FROM `SECTIONS` rather than written out a second time: a hand-kept
 * table beside the navigation is exactly how a group stops opening for a page
 * it lists. Longest-first is what puts `/admin/invoices/new` in Finanzen rather
 * than in Dashboard via the bare `/admin` prefix — the same longest-prefix rule
 * `adminRouteToTitle` already uses for the mobile top bar.
 *
 * `exact` mirrors `NavLink`'s `end`: `/admin` owns itself, not the whole admin
 * tree beneath it.
 *
 * `owns` is for the routes that exist WITHOUT a row of their own — a unit
 * detail reached from Einheiten, a work report opened out of Rapporte, the
 * handful of legacy aliases still routed. They belong to a group even though
 * nothing in the sidebar points at them, and a group that does not open for
 * the page the user is looking at is the whole failure this slice is about.
 */
/**
 * Whether a sidebar row is offered to a role.
 *
 * ADDITIVE AND OPT-IN. A row without `roles` is visible exactly as before — no
 * existing entry was reclassified when this landed, and none should be here:
 * hiding `Rechnungseinstellungen` or `Benutzer` from a manager is a product
 * decision about those pages, not a side effect of adding one new one.
 *
 * A row WITH `roles` is offered only to those roles. The one row that carries
 * it today is Automatisierungen, whose three endpoints are all
 * `require_roles("admin")` — so for a manager the link could only ever lead to
 * a refusal, which is the offer `AdminResponsibilityAreasPage` states the rule
 * against ("is offered no control that could only ever answer 403").
 *
 * AN UNKNOWN ROLE HIDES A GATED ROW rather than showing it, and that also
 * covers the moment before auth resolves: `user` is undefined on the first
 * render, so a gated row is absent rather than flashed and withdrawn. The safe
 * direction is the one where a row appears late, never the one where it appears
 * wrongly.
 *
 * Pure and exported so the rule can be asked directly, without rendering nine
 * sections to answer one question — the same reasoning
 * `getSidebarSectionForLocation` records for itself.
 */
export function isItemVisibleForRole(item, role) {
  if (!item || !Array.isArray(item.roles)) return true;
  return item.roles.includes(String(role || ""));
}

const SECTION_ROUTES = SECTIONS.flatMap((section) => {
  const rows = section.legacy
    ? [...section.items, ...section.legacy.items]
    : section.items;
  return [
    ...rows
      .filter((item) => item.to)
      .map((item) => ({ path: item.to, key: section.key, exact: !!item.end })),
    ...(section.owns || []).map((path) => ({
      path,
      key: section.key,
      exact: false,
    })),
  ];
}).sort((a, b) => b.path.length - a.path.length);

/**
 * The section that owns a location, or `null` when none does.
 *
 * Pure and exported: the accordion has exactly one question that is not a
 * rendering question, and this is it — so it is answered somewhere a test can
 * ask it directly rather than through nine rendered panels.
 *
 * `search` is load-bearing for one pair of rows. Kalender and Rapporte are the
 * same pathname told apart by `?mode=`, so neither carries a `to` to match on;
 * without asking `calendarNavState` the calendar would belong to no group at
 * all. Bexio is the other special case, in the opposite direction: the
 * workspace root owns EVERYTHING beneath it, `/admin/bexio/legacy` included,
 * and that page has no sidebar row to be matched by. `isBexioPath` is the
 * shared helper that already answers exactly that, and its docstring says this
 * is what it is for.
 */
export function getSidebarSectionForLocation(pathname, search) {
  const path = String(pathname || "");
  if (isBexioPath(path)) {
    return "bexio";
  }
  const calendar = calendarNavState(path, search);
  if (calendar.calendar || calendar.reports) {
    return "arbeitsorganisation";
  }
  for (const route of SECTION_ROUTES) {
    if (path === route.path) return route.key;
    if (!route.exact && path.startsWith(`${route.path}/`)) return route.key;
  }
  return null;
}

/* ─── Row presentation ─────────────────────────────────────────────── */

const ROW_BASE =
  "group flex items-center gap-2.5 rounded-[9px] px-2.5 py-[7px] text-[12.5px] no-underline transition-colors focus-visible:outline-none focus-visible:ring-2";

/**
 * One class factory for every row.
 *
 * The old sidebar had two — a "main" and a "sub" variant — and the only thing
 * that distinguished them was which row happened to be written first in each
 * group. That produced an indent and a colour step that meant nothing, so the
 * two are collapsed into one here: every navigable row is a peer of every other.
 */
function createRowClass(theme) {
  return function rowClass({ isActive }) {
    if (theme === "light") {
      if (isActive) {
        return `${ROW_BASE} bg-violet-500/10 font-semibold text-violet-700 ring-violet-500/60`;
      }
      return `${ROW_BASE} font-medium text-zinc-600 ring-violet-500/60 hover:bg-zinc-100 hover:text-zinc-900`;
    }
    if (isActive) {
      // `bg-violet-600/20` is the dark active surface the navigation has always
      // used; the redesign restyles around it rather than replacing it.
      return `${ROW_BASE} bg-violet-600/20 font-semibold text-violet-200 ring-violet-400/70`;
    }
    return `${ROW_BASE} font-medium text-slate-400 ring-violet-400/70 hover:bg-white/[0.06] hover:text-white`;
  };
}

function iconClass(theme, isActive) {
  if (theme === "light") {
    return isActive ? "text-violet-600" : "text-zinc-400 group-hover:text-zinc-600";
  }
  return isActive ? "text-violet-200" : "text-slate-500 group-hover:text-slate-300";
}

/**
 * A row's icon.
 *
 * `data-nav-icon` is kept from the previous implementation: it is how the tests
 * find the icon without depending on which library drew it. `aria-hidden` is not
 * optional — these icons repeat the label they sit next to, and announcing both
 * makes every entry read twice.
 */
function NavIcon({ icon: Icon, theme, isActive }) {
  return (
    <span className="flex shrink-0 items-center" data-nav-icon aria-hidden="true">
      <Icon className={`h-4 w-4 ${iconClass(theme, isActive)}`} strokeWidth={1.75} />
    </span>
  );
}

/** Icon + label, the shared row body. */
function RowBody({ icon, label, theme, isActive }) {
  return (
    <>
      <NavIcon icon={icon} theme={theme} isActive={isActive} />
      <span className="min-w-0 truncate">{label}</span>
    </>
  );
}

/* ─── Section ──────────────────────────────────────────────────────── */

/**
 * A section header.
 *
 * The header has to win against its own children without becoming a second kind
 * of loud. Four small steps do that, and they compound rather than any one of
 * them being turned up far enough to shout:
 *
 *   size     11px against the rows' 12.5px — still the smaller text, but no
 *            longer the 10px whisper that made the group read as a caption
 *   weight   bold against the rows' medium
 *   colour   a full step darker in light, a full step lighter in dark
 *   icon     18px against the rows' 16px, keeping its restrained accent
 *
 * `tracking-[0.08em]` rather than anything in `[0.5px .. 1.5px]`: `index.css`
 * repaints `uppercase` + those five exact tracking values with `!important`,
 * which would silently undo the colour chosen here.
 *
 * `isFirst` exists for the space ABOVE a section. Separating groups by pushing
 * them apart costs nothing visually, where a divider per group would add eight
 * new lines to a sidebar this dense. The gap below the header stays tight so
 * each header still reads as belonging to the rows under it, not floating
 * between two groups.
 */
function Section({ section, theme, open, onToggle, isFirst, children }) {
  const panelId = `${useId()}-${section.key}`;
  const Icon = section.icon;

  const headingText =
    theme === "light"
      ? "text-[11px] font-bold uppercase tracking-[0.08em] text-zinc-700"
      : "text-[11px] font-bold uppercase tracking-[0.08em] text-[#c3cfe2]";
  const headingBtn =
    theme === "light"
      ? "flex w-full items-center gap-2 rounded-[8px] bg-transparent px-2 py-[7px] text-left transition-colors hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60"
      : "flex w-full items-center gap-2 rounded-[8px] bg-transparent px-2 py-[7px] text-left transition-colors hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70";
  const chevron =
    theme === "light"
      ? "ml-auto h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform duration-150"
      : "ml-auto h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-150";

  return (
    <div
      className={isFirst ? "mb-0.5" : "mb-0.5 mt-3"}
      data-sidebar-section={section.key}
    >
      {/* Click only. There are deliberately NO pointer handlers here: a section
          that opened because the pointer passed over it rearranged the
          navigation under a user who was on their way somewhere else. The open
          section changes when a heading is CHOSEN, or when the route changes. */}
      <button
        type="button"
        onClick={onToggle}
        className={headingBtn}
        aria-expanded={open}
        aria-controls={panelId}
        style={{ cursor: "pointer" }}
      >
        {section.brandIcon ? (
          /* A brand mark, not an icon we draw. Three consequences:

             `rounded-[17%]` — the artwork is a rounded blue square baked onto an
             OPAQUE WHITE background (lossy WebP, no alpha channel), so rendered
             raw it shows four white wedges against the dark sidebar. The radius
             is measured from the source: its own corners turn at 94px of 554,
             so clipping at 17% follows the artwork's own curve rather than
             imposing a shape on it. The file is untouched — this is a mask, not
             an edit.

             `width`/`height` alongside the classes — the intrinsic size is
             554x554, and without them the row reflows on a slow load.

             `alt=""` AND `aria-hidden` — the heading already says "Bexio", and
             an alt of "Bexio" would announce the group twice. */
          <img
            src={section.brandIcon}
            alt=""
            aria-hidden="true"
            width={18}
            height={18}
            className="h-[18px] w-[18px] shrink-0 rounded-[17%]"
            data-brand-icon={section.key}
          />
        ) : (
          <Icon
            className={`h-[18px] w-[18px] shrink-0 ${section.accent[theme] || section.accent.dark}`}
            strokeWidth={1.75}
            aria-hidden="true"
          />
        )}
        {/* `min-w-0 truncate` rather than a bare `whitespace-nowrap`. At 11px the
            longest heading — "Vermieter & Verwaltungen" — measures 192px in a
            201px slot, so it fits on one line but with under 10px to spare.
            Nowrap alone would let a future longer heading shove the chevron out
            of the row; this way it degrades to an ellipsis instead. Nothing
            truncates at today's nine headings. */}
        <span className={`${headingText} min-w-0 truncate`}>{section.title}</span>
        <ChevronDown
          className={`${chevron} ${open ? "" : "-rotate-90"}`}
          strokeWidth={2}
          aria-hidden="true"
        />
      </button>

      {/* Unmounted rather than hidden when collapsed, exactly as before: the tests
          read "is this link present" from the DOM, and a hidden-but-present row
          would let a duplicate pass unnoticed. */}
      <div id={panelId} hidden={!open}>
        {open ? <div className="mt-0.5 grid gap-[3px] pl-1">{children}</div> : null}
      </div>
    </div>
  );
}

/* ─── Legacy finance block ─────────────────────────────────────────── */

/**
 * The pre-Bexio invoice world, nested inside Finanzen.
 *
 * STATIC LABELLING. It reads no capability, no provider field and no billing
 * state — it makes no request at all. Which engine creates NEW invoices is a
 * question the invoice pages already ask and fail closed on; repeating it here
 * would put a second, slower opinion about the same fact in the navigation.
 *
 * Archive, not alarm: rose at low opacity with a quiet note, deliberately unlike
 * the amber warning and red error surfaces used elsewhere in the product.
 */
function LegacyFinanceBlock({ legacy, theme, renderItem }) {
  const shell =
    theme === "light"
      ? "mt-1.5 rounded-[11px] border border-rose-200/80 bg-rose-50/60 p-1.5"
      : "mt-1.5 rounded-[11px] border border-rose-400/20 bg-rose-500/[0.07] p-1.5";
  const badge =
    theme === "light"
      ? "rounded-[5px] border border-rose-200 bg-rose-100/70 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.08em] text-rose-700"
      : "rounded-[5px] border border-rose-400/25 bg-rose-500/15 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.08em] text-rose-200";
  const note =
    theme === "light"
      ? "px-1.5 pb-0.5 pt-1 text-[9.5px] leading-snug text-rose-700/80"
      : "px-1.5 pb-0.5 pt-1 text-[9.5px] leading-snug text-rose-200/70";

  return (
    <div className={shell} data-legacy-finance>
      <div className="flex items-center gap-1.5 px-1.5 pb-1 pt-0.5">
        <span className={badge}>{legacy.badge}</span>
      </div>
      <div className="grid gap-[3px]">{legacy.items.map(renderItem)}</div>
      <p className={note}>{legacy.note}</p>
    </div>
  );
}

/* ─── User helpers ─────────────────────────────────────────────────── */

function userInitials(user) {
  if (!user) return "?";
  const n = String(user.full_name || "").trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
    }
    return n.slice(0, 2).toUpperCase();
  }
  const e = String(user.email || "").trim();
  return e.slice(0, 2).toUpperCase() || "?";
}

/**
 * SidebarBody renders the inner sidebar contents (logo, org card, nav,
 * footer) WITHOUT the outer `min-h-screen w-[288px]` shell. The default
 * AdminSidebar export wraps SidebarBody in the persistent shell exactly
 * as before. The mobile/tablet drawer in AdminLayout reuses SidebarBody
 * directly so it can fit inside a Vaul drawer's own height/width.
 *
 * Drawer auto-close on navigate is handled in AdminLayout via a
 * `useEffect` keyed on `location.pathname` — that catches both NavLink
 * clicks and any future programmatic navigation triggered from inside
 * the drawer (logout, theme toggle, etc.).
 */
export function SidebarBody() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const rowClass = createRowClass(theme);

  // Kalender and Rapporte are one route told apart by ?mode= — see
  // workOrganizationNav for why NavLink cannot decide between them.
  const calendarNav = calendarNavState(location.pathname, location.search);

  /**
   * ONE open section, never two.
   *
   * The previous model was a flag per group, which let all nine stand open at
   * once and made the sidebar taller than the viewport on the routes that need
   * it most. `null` means everything is collapsed, and only a deliberate click
   * on the heading of the open section produces it.
   *
   * The location is stored ALONGSIDE the section so the route can take the
   * group back after a manual detour, and that adjustment is made during render
   * rather than in an effect. An effect would commit one frame with the
   * previous group still open, which reads as a flicker on every navigation —
   * the same reason the calendar's mode is read during render rather than
   * synchronised afterwards.
   */
  const routeSection = getSidebarSectionForLocation(location.pathname, location.search);
  const locationKey = `${location.pathname}${location.search}`;
  const [open, setOpen] = useState(() => ({ locationKey, section: routeSection }));

  const routeChanged = open.locationKey !== locationKey;
  // A route no group claims — a legacy alias, a page opened by URL — leaves the
  // sidebar as the user left it. Collapsing everything would punish them for
  // going somewhere the navigation does not list.
  const openSection = routeChanged
    ? routeSection === null
      ? open.section
      : routeSection
    : open.section;
  if (routeChanged) {
    setOpen({ locationKey, section: openSection });
  }

  /**
   * The only way to change the open section by hand.
   *
   * An earlier version of this also opened a section after the pointer rested
   * on its heading for 200ms. It was removed: moving the mouse ACROSS the
   * sidebar on the way to the page content rearranged the navigation behind the
   * cursor, and no delay short enough to feel responsive is long enough to tell
   * "reading this group" apart from "passing over it". Opening a group is now
   * something a person does, not something that happens to them.
   */
  const toggleSection = (key) =>
    setOpen({ locationKey, section: openSection === key ? null : key });

  const handleLogout = () => {
    logout().then(() => navigate("/admin/login", { replace: true }));
  };

  /**
   * One row.
   *
   * Kalender and Rapporte are the SAME pathname with a different query, and
   * React Router matches `NavLink` on the path alone — so as `NavLink`s both
   * would report themselves active at once and the navigation would claim the
   * user is in two places. They get a plain `Link` and an explicit
   * `aria-current` computed from the query instead.
   */
  const renderItem = (item) => {
    if (item.kind === "calendar" || item.kind === "reports") {
      const isActive =
        item.kind === "calendar" ? calendarNav.calendar : calendarNav.reports;
      return (
        <Link
          key={item.kind}
          to={item.kind === "calendar" ? "/admin/calendar" : REPORTS_TO}
          className={rowClass({ isActive })}
          aria-current={isActive ? "page" : undefined}
        >
          <RowBody icon={item.icon} label={item.label} theme={theme} isActive={isActive} />
        </Link>
      );
    }
    return (
      <NavLink key={item.to} to={item.to} end={item.end} className={rowClass}>
        {({ isActive }) => (
          <RowBody icon={item.icon} label={item.label} theme={theme} isActive={isActive} />
        )}
      </NavLink>
    );
  };

  const footerBorder =
    theme === "light" ? "border-t border-zinc-200" : "border-t border-white/[0.08]";

  const secondaryBtn =
    theme === "light"
      ? "inline-flex items-center justify-center gap-1.5 rounded-[9px] border border-zinc-200 bg-white px-3 py-2 text-[12px] font-semibold text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60"
      : "inline-flex items-center justify-center gap-1.5 rounded-[9px] border border-white/[0.09] bg-white/[0.03] px-3 py-2 text-[12px] font-semibold text-[#8b9ab5] transition-colors hover:bg-white/[0.07] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/70";

  return (
    <>
      {/* ── Vantio brand. LOCKED: unchanged from before the redesign. ── */}
      <div className="mb-3 shrink-0">
        <h2 className="m-0">
          <span style={{ fontSize: "20px", fontWeight: 800, letterSpacing: "-0.7px" }}>
            Van
            <span
              style={{
                background: "linear-gradient(135deg, #5b8cff, #9d7bff)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              tio
            </span>
          </span>
        </h2>
      </div>

      {/* WHERE you are. The only organization card in the sidebar — a second one
          used to sit above the session controls repeating this one word for word.

          `organization_sidebar_icon_url`, NOT `organization_logo_url`: the latter
          is the invoice letterhead mark, so until the backend grew a separate
          field, choosing a sidebar icon meant restyling every invoice the
          organization sends. Nothing here knows which organization it is
          rendering. */}
      {user ? (
        <div className="mb-4 shrink-0">
          <OrganizationIdentity
            theme={theme}
            name={user.organization_name}
            logoUrl={user.organization_sidebar_icon_url}
            organizationId={user.organization_id}
            showCopy
          />
        </div>
      ) : null}

      <nav
        className="flex min-h-0 flex-1 flex-col gap-0 overflow-y-auto pr-0.5"
        aria-label="Hauptnavigation"
      >
        {SECTIONS.map((section, index) => (
          <Section
            key={section.key}
            section={section}
            theme={theme}
            open={openSection === section.key}
            onToggle={() => toggleSection(section.key)}
            isFirst={index === 0}
          >
            {section.items
              .filter((item) => isItemVisibleForRole(item, user?.role))
              .map(renderItem)}
            {section.legacy ? (
              <LegacyFinanceBlock
                legacy={section.legacy}
                theme={theme}
                renderItem={renderItem}
              />
            ) : null}
          </Section>
        ))}
      </nav>

      {/* WHO you are. The organization card that used to head this block was the
          same card as the one at the top, and the copy-id control it carried has
          moved up there with it. */}
      <div className={`mt-auto shrink-0 pt-3 ${footerBorder}`}>
        {user ? (
          <div className="mb-2.5 flex items-center gap-2.5 px-1">
            <div
              className={
                theme === "light"
                  ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-zinc-100 text-[11px] font-bold text-zinc-800"
                  : "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/[0.09] bg-white/[0.06] text-[11px] font-bold text-[#f1f5f9]"
              }
              aria-hidden="true"
            >
              {userInitials(user)}
            </div>
            <div className="min-w-0 flex-1">
              <div
                className={
                  theme === "light"
                    ? "truncate text-[12px] font-semibold text-zinc-900"
                    : "truncate text-[12px] font-semibold text-[#f1f5f9]"
                }
              >
                {user.full_name || "—"}
              </div>
              <div
                className={
                  theme === "light"
                    ? "truncate text-[10px] text-zinc-500"
                    : "truncate text-[10px] text-[#8b9ab5]"
                }
              >
                {user.email}
              </div>
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-2">
          {/* aria-pressed rather than a label that flips: the control IS the
              dark-mode switch, and its pressed state is what changes. */}
          <button
            type="button"
            onClick={toggleTheme}
            className={secondaryBtn}
            aria-label="Dunkelmodus umschalten"
            aria-pressed={theme === "dark"}
          >
            {theme === "dark" ? (
              <Sun className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            ) : (
              <Moon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            )}
            {theme === "dark" ? "Hell" : "Dunkel"}
          </button>
          <button type="button" onClick={handleLogout} className={secondaryBtn}>
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
            Abmelden
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * AdminSidebar — persistent shell. At lg:+ this is mounted directly by
 * AdminLayout exactly as before. Below lg: AdminLayout renders
 * SidebarBody inside a Vaul left-drawer instead.
 *
 * The width lives in `SIDEBAR_WIDTH_CLASS` because AdminLayout's drawer has to
 * match it exactly: two hand-written widths is how the drawer and the sidebar
 * drift apart by a few pixels and nobody notices until a label wraps in one and
 * not the other.
 */
export const SIDEBAR_WIDTH_CLASS = "w-[288px]";

function AdminSidebar() {
  const { theme } = useTheme();

  const sidebarShell =
    theme === "light"
      ? `box-border flex min-h-screen ${SIDEBAR_WIDTH_CLASS} flex-col border-r border-zinc-200 bg-white px-3.5 pb-4 pt-6 text-zinc-900`
      : `box-border flex min-h-screen ${SIDEBAR_WIDTH_CLASS} flex-col border-r border-solid border-white/[0.08] bg-[#0b1220] px-3.5 pb-4 pt-6 text-[#f8fafc]`;

  return (
    <div className={sidebarShell}>
      <SidebarBody />
    </div>
  );
}

export default AdminSidebar;
